import { Flex } from "@amzn/storm-ui";
import { useAsync } from "@react-hookz/web";
import { isEmpty } from "lodash";
import React, { useEffect, useMemo, type ReactElement } from "react";
import styled from "styled-components";
import { useStore } from "zustand";

import { getBlinkApiClientV2 } from "../../../api/blinkApi/blinkApi";
import { useAsyncErrorStatusNotification } from "../../../hooks/notifications/useAsyncErrorStatusNotification";
import { FeatureFlags, FeatureFlagsStore } from "../../../store/featureFlags";
import { navigationStore } from "../../../store/navigation";
import { salesforceStore } from "../../../store/salesforce";
import { isLoading } from "../../../utils/api.utils";
import {
    getAdvertiserId,
    getAdvertiserName,
    isValidAdvertiserId,
} from "../../../utils/permission.utils";
import { isAllInsightsTab } from "../../../utils/tab.utils";
import { getValueFromSearchParams } from "../../../utils/window.utils";
import { UnifiedSearchToggle } from "../../common/UnifiedSearchToggle";
import { AccountSwitcher } from "../../customAccountPicker/AccountSwitcher";

import { ContextItem } from "./ContextItem";

const StyledSfContextWrapper = styled(Flex)`
    margin-top: 12px;
    width: 100%;
`;

export const SfContext = (): ReactElement | null => {
    const advertiserId = getAdvertiserId();
    const advertiserName = getAdvertiserName();
    const isValidId = isValidAdvertiserId();

    const [useUnifiedSearch] = useStore(FeatureFlagsStore, (state) => [
        state.unifiedSearch,
    ]);
    const isUnifiedSearchFlagInUrl = !!getValueFromSearchParams(
        FeatureFlags.UNIFIED_SEARCH,
    );

    const [sfContext, setSfContext, setAsyncStatus] = useStore(
        salesforceStore,
        (state) => [state.sfContext, state.setSfContext, state.setAsyncStatus],
    );
    const [activeTab] = useStore(navigationStore, (state) => [state.activeTab]);

    const { name, locale, vertical, subvertical } = sfContext;

    const [sfState, sfAction] = useAsync(async () => {
        const { data } = await (
            await getBlinkApiClientV2()
        ).getSfContext({
            advertiserId,
        });

        if (data) {
            setSfContext(data);
        }
    });
    useEffect(() => setAsyncStatus(sfState.status), [sfState.status]);

    const loading = isLoading(sfState.status);

    useAsyncErrorStatusNotification(sfState);

    useEffect(() => {
        if (isValidId) {
            void sfAction.execute();
        }
    }, [isValidId]);

    const contextItems = useMemo(() => {
        return [
            {
                isLoading: loading,
                label: "Salesforce Account",
                value: isEmpty(name) ? (advertiserName ?? advertiserId) : name,
            },
            ...(isAllInsightsTab(activeTab)
                ? [
                      {
                          isLoading: loading,
                          label: "Marketplace",
                          value: locale,
                      },
                      {
                          isLoading: loading,
                          label: "Vertical",
                          value: vertical,
                      },
                      {
                          isLoading: loading,
                          label: "Sub Vertical",
                          value: subvertical,
                      },
                  ]
                : []),
        ];
    }, [loading, locale, vertical, subvertical, advertiserId, activeTab]);

    return (
        <StyledSfContextWrapper
            columnGap="base"
            wrap="wrap"
            alignItems="center"
            justifyContent="space-between"
            data-testid="sf-context"
        >
            <Flex columnGap="base" wrap="wrap" alignItems="center">
                <AccountSwitcher />
                {isValidId &&
                    contextItems.map(({ isLoading, label, value }) => (
                        <ContextItem
                            key={label}
                            isLoading={isLoading}
                            label={label}
                            value={value}
                        />
                    ))}
            </Flex>
            {(useUnifiedSearch || isUnifiedSearchFlagInUrl) &&
                isAllInsightsTab(navigationStore.getState().activeTab) && (
                    <UnifiedSearchToggle />
                )}
        </StyledSfContextWrapper>
    );
};
